import { Injectable } from '@angular/core';
import { ConfigService } from '../config.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { User } from '../../../shared/models/user';
import { Subscription, Observable, BehaviorSubject, of, lastValueFrom, catchError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LoaderService } from '../loader/loader.service';
import { ClientsList } from 'src/app/shared/models/app.config';
import { Common } from '../../common/common';
import { Constants } from 'src/app/shared/constants';
import { ClientInsuredCategoryModel } from 'src/app/shared/models/client-insured-category.model';
import { ClientLocationModel } from 'src/app/shared/models/client-location.model';
import { PolicyInfo } from 'src/app/shared/models/policy-info.model';
import { APIEndPoints } from 'src/app/shared/APIEndPoints';

@Injectable({
  providedIn: 'root'
})
export class ClientService {

  currentUser: User;
  currentClientNumber: number;
  currentClientId: number;
  currentUserSubscription: Subscription;

  // Tracks how many times each method has been invoked within the app session.
  private callCounters: Record<string, number> = {};

  private trackCall(method: string): number {
    this.callCounters[method] = (this.callCounters[method] ?? 0) + 1;
    return this.callCounters[method];
  }

  // Client List
  private clientListSubject = new BehaviorSubject<any>(null);
  clientList = this.clientListSubject.asObservable();

  // Selected Client
  currentClientKey: string;
  private selectedClientSubject: BehaviorSubject<any>
  selectedClient: Observable<any>;

  secureApiUrl = this.configService.apiUri;
  waiverApiUrl = this.configService.waiverApiUri;
  private moduleChangedSubject = new BehaviorSubject<any>(null);
  moduleChanged = this.moduleChangedSubject.asObservable();
  private hideClientListSubject = new BehaviorSubject<boolean>(true);
  hideClientList = this.hideClientListSubject.asObservable();
  private moduleWiseClientsSubject = new BehaviorSubject<ClientsList>(null);
  moduleWiseClients = this.moduleWiseClientsSubject.asObservable();
  private handleSchoolSelectSubject = new BehaviorSubject<boolean>(true);
  handleSchoolSelect = this.handleSchoolSelectSubject.asObservable();
  private maxPolicyYearSubject = new BehaviorSubject<any>(null);
  maxPolicyYear = this.maxPolicyYearSubject.asObservable();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private authenticationService: AuthenticationService,
    private loaderService: LoaderService
  ) {
    this.currentClientKey = this.configService.currentClientKey;

    this.selectedClientSubject = localStorage.getItem(this.currentClientKey) ?
      new BehaviorSubject<any>(this.getClientFromLocalStorage()) :
      new BehaviorSubject<any>(null);
    this.selectedClient = this.selectedClientSubject.asObservable();
    this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
      this.currentUser = user;
    });
  }

  private getClientFromLocalStorage() {
    const hashClient = localStorage.getItem(this.currentClientKey);
    if (hashClient) {
      const jsonClient = JSON.parse(atob(hashClient));
      if (jsonClient) {
        jsonClient.checked = false;
        return jsonClient;
      }
    }
    return null;
  }

  setClientList(clients: any) {
    this.clientListSubject.next(clients);
  }

  currentSelectedClient(): any {
    return this.selectedClientSubject.value;
  }

  setSelectedClient(client: any) {
    const before = this.selectedClientSubject.value;
    const beforeInfo = before ? { Id: before.Id, Number: before.Number, Name: before.Name } : null;
    this.selectedClientSubject.next(client);
    if (client) {
      this.currentClientNumber = client.Number;
      this.currentClientId = client.Id;
      localStorage.setItem(this.currentClientKey, btoa(JSON.stringify(client)));
    } else {
      localStorage.removeItem(this.currentClientKey);
    }
  }
  setModuleWiseClients(client: any) {
    this.moduleWiseClientsSubject.next(client);
  }
  handlePolicySpinner(val: boolean) {
    this.loaderService.handlePolicySpinner(val);
  }
  handleClientList(con: boolean) {
    this.hideClientListSubject.next(con);
  }

  getSelectedClient() {
    return this.currentClientNumber;
  }
  moduleChangedevent(module: any) {
    this.moduleChangedSubject.next(module);
  }
  handleSchoolSelectPage(con: boolean) {
    this.handleSchoolSelectSubject.next(con);
  }
  setMaxPolicyYear(val: number) {
    this.maxPolicyYearSubject.next(val);
  }
  createClientList(clientsData: any, filterClients: Number[] = null): void {
    const moduleWiseClientsList = new ClientsList();
    moduleWiseClientsList.InsuranceManagement = clientsData?.InsuranceManagement;
    moduleWiseClientsList.PaymentManagement = clientsData?.PaymentManagement;
    moduleWiseClientsList.WaiverManagement = clientsData?.WaiverManagement;
    moduleWiseClientsList.SchoolSetup = clientsData?.SchoolSetup;
    // Added condition for internal verification client admin to have only 3rd party enabled clients only
    if (filterClients) {
      moduleWiseClientsList.WaiverManagement = filterClients.length > 0 ?
        clientsData?.WaiverManagement?.filter(f => filterClients.includes(f.Id)) : [];
      // As per the discussions with BAs Internal verification will have only access to Waiver management so dashborad clients should be in sync with waiver clients Updating on behalf of Youtrack PC-14 improvemnts.
      //moduleWiseClientsList.AllClients = moduleWiseClientsList.WaiverManagement;
    }
    moduleWiseClientsList.ManageSHCBilling = clientsData?.ClaimsManagement?.ManageSHCBilling;
    moduleWiseClientsList.ManageReferrals = clientsData?.ClaimsManagement?.ManageReferrals;
    const claimsManagement = moduleWiseClientsList?.ManageSHCBilling ? moduleWiseClientsList?.ManageSHCBilling.concat(moduleWiseClientsList?.ManageReferrals) : moduleWiseClientsList?.ManageReferrals;
    if (claimsManagement?.length > 0)
      moduleWiseClientsList.ClaimsManagement = Common.sortByProperty<any>('Name', Common.removeDuplicatesObjectFromJsonArrayByProp(claimsManagement, 'Id'));
    if (moduleWiseClientsList.WaiverManagement)
      moduleWiseClientsList.AllClients = moduleWiseClientsList.WaiverManagement;
    if (moduleWiseClientsList.PaymentManagement)
      moduleWiseClientsList.AllClients = moduleWiseClientsList.AllClients ? moduleWiseClientsList.AllClients.concat(moduleWiseClientsList.PaymentManagement) : moduleWiseClientsList.PaymentManagement;
    if (moduleWiseClientsList.InsuranceManagement)
      moduleWiseClientsList.AllClients = moduleWiseClientsList.AllClients ? moduleWiseClientsList.AllClients.concat(moduleWiseClientsList.InsuranceManagement) : moduleWiseClientsList.InsuranceManagement;
    if (moduleWiseClientsList.ClaimsManagement)
      moduleWiseClientsList.AllClients = moduleWiseClientsList.AllClients ? moduleWiseClientsList.AllClients.concat(moduleWiseClientsList.ClaimsManagement) : moduleWiseClientsList.ClaimsManagement;

    moduleWiseClientsList.AllClients = Common.sortByProperty<any>('Name', Common.removeDuplicatesObjectFromJsonArrayByProp(moduleWiseClientsList.AllClients, 'Id'));

    if (Object.keys(moduleWiseClientsList).includes(Constants.WaiverManagement) && moduleWiseClientsList?.WaiverManagement) {
      moduleWiseClientsList.AllClients?.forEach(element => {
        const waiverClients = moduleWiseClientsList.WaiverManagement.find(c => c.Id === element.Id);
        if (waiverClients) {
          element[Constants.EnrollmentEnabled] = waiverClients[Constants.EnrollmentEnabled];
          element[Constants.EarlyEnrollment] = waiverClients[Constants.EarlyEnrollment];
          element[Constants.AutoConversionEnabled] = waiverClients[Constants.AutoConversionEnabled];
          element[Constants.EmailNotificationEnabled] = waiverClients[Constants.EmailNotificationEnabled];
        }
      });
    }
    this.loaderService.hide();
    this.loaderService.handlePolicySpinner(false);
    this.setModuleWiseClients(moduleWiseClientsList);
  }

  public getAll(moduleName: string): Observable<any> {
    let url = this.secureApiUrl + `/api/Clients/GetClientsByUsername?username=` + this.currentUser.profile.UserName;
    if (moduleName) {
      url += '&module=' + moduleName;
    }
    return this.http.post<any>(url, {});
  }



  public getClientCustomIndicators(settingName: string, clientId: number): Observable<any> {
    let url = this.secureApiUrl + `/api/Clients/GetClientCustomIndicators?clientNum=${clientId}&settingNames=${settingName}`;
    return this.http.post<any>(url, null, {}); {
    }
  }

  public getInsuredCategories(clientId: number): Observable<ClientInsuredCategoryModel[]> {
    let url = this.secureApiUrl + `/api/Clients/${clientId}/InsuredCategories`;
    return this.http.get<ClientInsuredCategoryModel[]>(url, {}); {
    }
  }

  public getClientLocations(clientId: number): Observable<ClientLocationModel[]> {
    let url = this.secureApiUrl + '/api/Clients/GetClientLocationsByCriteria';
    const criteriaObj =
    {
      Module: "WaiverManagement",
      clientNum: 0,
      clientId: clientId,
      Active: true
    };
    return this.http.post<ClientLocationModel[]>(url, criteriaObj, {}); {
    }
  }

  public getPolicyYears(clientNumber: number) {
    this.loaderService.handlePolicySpinner(true);
    const url = this.secureApiUrl + `/api/Clients/GetClientCustomTexts?clientNum=${clientNumber}&settingNames=PolicyYear`;
    return this.http.post<any>(url, null, { headers: { 'showLoader': 'false' } });
  }

  public getPolicyInfo(policyYear: number, clientNumber: number) {
    const url = `${this.secureApiUrl}/api/Policy/GetClientPolicies?policyYear=${policyYear}&clientNumber=${clientNumber}`;
    return this.http.get<PolicyInfo[]>(url);
  }

  public getGenders(clientId: number) {
    const url = this.secureApiUrl + '/api/Clients/' + clientId + '/Genders';
    return this.http.get<any>(url);
  }

  public getCoverages(insuredId: number, primaryInsuredId: number){
    
    if (primaryInsuredId == null || insuredId == null)
    {
      return null;
    }

    const url = this.secureApiUrl + '/api/Coverage/GetCoverages?insuredId=' + insuredId + "&primaryInsuredId=" + primaryInsuredId;
    return this.http.get<any>(url);
  }
  public getThirdPartyVerificationEnabledClientIds(): Observable<Number[]> {
    return this.http.get<Number[]>(`${this.waiverApiUrl}${APIEndPoints.getThirdPartyVerificationEnabledClientIds}`).pipe(
      catchError(Common.handleError)
    );
  }
  public getAppealEnabledClientIds(): Observable<Number[]> {
    return this.http.get<Number[]>(`${this.waiverApiUrl}${APIEndPoints.getAppealEnabledClientIds}`).pipe(
      catchError(Common.handleError)
    );
  }
  filterThirdPartyVerificationEnabledClients(allowedClients: any): void {
    this.getThirdPartyVerificationEnabledClientIds().subscribe({
      next: (res) => {
        if (res.length > 0) {
          this.createClientList(allowedClients, res);
        }
      },
      error: (error) => console.error(`error : ${error}`),
      complete: () => {
      }
    });
  }

  public getPolicyInfoForAddInsuredCoverage(policyYear: number, clientNumber: number, optionNumber: number) {
    const url = this.secureApiUrl + '/api/Policy/GetClientPoliciesForAddInsuredCoverage?policyYear=' + policyYear + '&clientNumber=' + clientNumber + '&optionNumber=' + optionNumber;
    return this.http.get<PolicyInfo[]>(url);
  }
}
